import { Dialog, Transition } from "@headlessui/react";
import { ExclamationCircleIcon, XIcon } from "@heroicons/react/outline";
import { Fragment, useCallback, useRef, useState } from "react";
import { ModalConfig } from "../../context/toast/ToastProvider";
import classNames from "../../utils/classNames";
import CenteredLoading from "../CenteredLoading";

interface Props {
  config: ModalConfig;
  setConfig: React.Dispatch<React.SetStateAction<ModalConfig>>;
}

const ModalWarning: React.FC<Props> = ({ config, setConfig }) => {
  const onClose = useCallback(() => {
    setConfig({ ...config, open: false });
  }, [config, setConfig]);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = useState(false);

  const onTrigger = useCallback(
    async (type: "cancel" | "resolve") => {
      const { cancel, resolve } = config;

      if (type === "cancel" && cancel) {
        cancel();
      }

      if (type === "resolve" && resolve) {
        setLoading(true);

        await resolve();

        setLoading(false);
      }

      onClose();
    },
    [config, onClose]
  );

  return (
    <Transition.Root show={config.open} as={Fragment}>
      <Dialog
        as="div"
        open={config.open}
        className="fixed z-50 inset-0 overflow-y-auto"
        onClose={() => onTrigger("cancel")}
        initialFocus={cancelButtonRef}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full px-4 pb-4 sm:p-6 dark:bg-gray-800">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  ref={cancelButtonRef}
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                  onClick={() => onTrigger("cancel")}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:text-left mb-6">
                <div className="flex items-center">
                  {config.withoutIcon !== true && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 mr-1 dark:text-white"
                      aria-hidden="true"
                    />
                  )}
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                  >
                    {config.title || "Delete confirmation"}
                  </Dialog.Title>
                </div>
                <div className="mt-4 text-left">
                  <span className="text-sm text-gray-800 dark:text-gray-300">
                    {config.message ||
                      "Are you sure you want to delete this data? This is a permanent action that cannot be undone."}
                  </span>
                </div>
              </div>
              <div className="flex flex-col-reverse md:flex-row-reverse md:gap-3">
                {!config.withoutSaveButton && (
                  <button
                    type="button"
                    disabled={loading}
                    className={classNames(
                      config.confirmButtonClassname ||
                        "bg-red-600 hover:bg-red-700",
                      "w-full flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-sm font-medium text-white focus:outline-none sm:text-sm hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (loading) return;

                      onTrigger("resolve");
                    }}
                  >
                    {loading && <CenteredLoading className="w-4 h-4 mr-2" />}
                    {loading && config.loadingButtonText
                      ? config.loadingButtonText
                      : config.confirmButtonText || "Delete"}
                  </button>
                )}

                {!config.withoutCancelButton && (
                  <button
                    type="button"
                    className={classNames(
                      config.cancelButtonClassname ||
                        "dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 border-gray-300 text-gray-700",
                      "w-full bg-white py-2 px-4 mb-3 border rounded-md shadow-sm text-sm font-medium md:mb-0 hover:opacity-75 focus:outline-none"
                    )}
                    onClick={() => onTrigger("cancel")}
                  >
                    {config.cancelButtonText || "Cancel"}
                  </button>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalWarning;
